import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft } from "lucide-react";

export default function TopicDetail() {
  const { topicId } = useParams();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { user } = useAuth();

  const load = async () => {
    const { data } = await api.get(`/topics/${topicId}`);
    setData(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/topics/${topicId}/replies`, { body: reply });
      setReply("");
      toast.success("Cevabın eklendi");
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Cevap eklenemedi");
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <div className="k-container py-16">
        <div className="k-card h-40 animate-pulse" />
      </div>
    );
  }

  const { topic, replies } = data;

  return (
    <div className="k-container py-12 md:py-16">
      <Link to={`/forum/${topic.category}`} className="k-btn-ghost text-sm" data-testid="back-to-category">
        <ArrowLeft className="mr-1 h-4 w-4" /> Kategoriye dön
      </Link>

      <article className="k-card mt-4 p-8">
        <h1 className="font-display text-4xl text-ink-900 md:text-5xl" data-testid="topic-title">
          {topic.title}
        </h1>
        <div className="mt-3 flex items-center gap-3 font-body text-sm text-ink-500">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-sage-500 text-cream-50">
            {topic.author_name?.[0]?.toUpperCase()}
          </span>
          <span>
            {topic.author_name} · {new Date(topic.created_at).toLocaleDateString("tr-TR")}
          </span>
        </div>
        <p className="mt-6 whitespace-pre-wrap font-body text-lg leading-relaxed text-ink-900" data-testid="topic-body">
          {topic.body}
        </p>
      </article>

      <h2 className="mt-10 font-heading text-2xl font-bold text-ink-900" data-testid="replies-heading">
        Cevaplar ({replies.length})
      </h2>
      <div className="mt-4 space-y-3">
        {replies.length === 0 && (
          <div className="rounded-2xl border border-dashed border-cream-300 p-8 text-center text-ink-500">
            Henüz cevap yok — ilk cümleyi sen ekle.
          </div>
        )}
        {replies.map((r) => (
          <div key={r.reply_id} className="k-card p-6" data-testid={`reply-${r.reply_id}`}>
            <div className="flex items-center gap-3 text-sm text-ink-500">
              <span className="grid h-7 w-7 place-items-center rounded-full bg-earth-400 text-cream-50 text-xs font-bold">
                {r.author_name?.[0]?.toUpperCase()}
              </span>
              <span className="font-body">
                {r.author_name} · {new Date(r.created_at).toLocaleDateString("tr-TR")}
              </span>
            </div>
            <p className="mt-3 whitespace-pre-wrap font-body text-ink-900">{r.body}</p>
          </div>
        ))}
      </div>

      {user ? (
        <form onSubmit={submit} className="k-card mt-8 p-6" data-testid="reply-form">
          <label className="k-label">Cevap yaz</label>
          <textarea
            rows={4}
            className="k-input resize-none"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Düşüncelerini yaz…"
            data-testid="reply-input"
          />
          {error && (
            <div className="mt-3 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}
          <div className="mt-4 flex justify-end">
            <button type="submit" disabled={busy || !reply.trim()} className="k-btn-primary disabled:opacity-60" data-testid="reply-submit">
              {busy ? "Gönderiliyor…" : "Cevabı gönder"}
            </button>
          </div>
        </form>
      ) : (
        <div className="k-card mt-8 p-6 text-center text-ink-700" data-testid="reply-login-cta">
          Cevap yazmak için{" "}
          <Link to="/login" className="font-bold text-sage-700 hover:underline">
            giriş yap
          </Link>
          .
        </div>
      )}
    </div>
  );
}
